/**
 * Centralized Image Assets
 * 
 * This file provides a single source of truth for all image assets in the application.
 * Benefits:
 * - Shorter import paths
 * - Type safety and autocomplete
 * - Easy refactoring - change path in one place
 * - Clear organization by purpose
 */

// Helper function to require images with error handling
const requireImage = (path) => {
  try {
    return require(`./images/${path}`);
  } catch (error) {
    console.warn(`Image not found: ${path}`);
    return null;
  }
};

export const IMAGES = {
  // Brand assets - logos, icons, corporate identity
  branding: {
    logoMain: requireImage('branding/logo-main.png'),
    logoAlt: requireImage('branding/logo-alt.jpeg'),
    appIcon: requireImage('branding/app-icon.png'),
  },

  // UI elements - backgrounds, decorative images, QR codes
  ui: {
    backgroundHero: requireImage('ui/background-hero.jpeg'),
    qrCodeWebsite: requireImage('ui/qr-code-website.png'),
  },

  // Store badges for app downloads
  badges: {
    appStore: {
      ar: requireImage('badges/app-store/app-store-badge-ar.svg'),
      en: requireImage('badges/app-store/app-store-badge-en.svg'),
    },
    googlePlay: {
      ar: requireImage('badges/google-play/google-play-badge-ar.png'),
      en: requireImage('badges/google-play/google-play-badge-en.png'),
    },
  },

  // App screenshots for showcasing features
  screenshots: {
    home: requireImage('screenshots/screenshot-home.png'),
    news: requireImage('screenshots/screenshot-news.png'),
    bills: requireImage('screenshots/screenshot-bills.png'),
    contact: requireImage('screenshots/screenshot-contact.png'),
  },
};

// Helper functions for common image operations
export const getAppStoreBadge = (language = 'en') => {
  return IMAGES.badges.appStore[language] || IMAGES.badges.appStore.en;
};

export const getGooglePlayBadge = (language = 'en') => {
  return IMAGES.badges.googlePlay[language] || IMAGES.badges.googlePlay.en;
};

// Get all screenshots as an array for gallery/carousel components
export const getAllScreenshots = () => {
  return Object.values(IMAGES.screenshots).filter(Boolean);
};

// Get screenshot by key with fallback
export const getScreenshot = (key) => {
  return IMAGES.screenshots[key] || IMAGES.screenshots.home;
};

// Image metadata for better accessibility and SEO
export const IMAGE_ALT_TEXTS = {
  branding: {
    logoMain: 'Litani Gateway - Municipality Logo',
    logoAlt: 'Litani Gateway - Alternative Logo',
    appIcon: 'Litani Gateway App Icon',
  },
  ui: {
    backgroundHero: 'Municipality building background',
    qrCodeWebsite: 'QR Code to access website',
  },
  badges: {
    appStore: {
      ar: 'تحميل من App Store',
      en: 'Download on the App Store',
    },
    googlePlay: {
      ar: 'تحميل من Google Play',
      en: 'Get it on Google Play',
    },
  },
  screenshots: {
    home: 'App Homepage Screenshot',
    news: 'News Section Screenshot',
    bills: 'Bills Payment Screenshot',
    contact: 'Contact Form Screenshot',
  },
};

// Get alt text for image with language support
export const getImageAltText = (category, key, language = 'en') => {
  const altText = IMAGE_ALT_TEXTS[category]?.[key];
  
  if (typeof altText === 'object' && altText[language]) {
    return altText[language];
  }
  
  return typeof altText === 'string' ? altText : `${category} ${key} image`;
};

export default IMAGES;